import React, { useRef, useState } from 'react';
import './ChapterAudioPlayer.css';

const SKIP_SECONDS = 15;

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }

  const whole = Math.floor(seconds);

  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

const ChapterAudioPlayer = ({ src, heading }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = () => {
    const audio = audioRef.current;

    if (audio.paused) {
      audio.play().catch(() => {
        // Playback refused (e.g. the file failed to load): the button simply stays on "play".
      });
    } else {
      audio.pause();
    }
  };

  const seekTo = (seconds) => {
    const audio = audioRef.current;
    const end = Number.isFinite(audio.duration) ? audio.duration : seconds;

    audio.currentTime = Math.max(0, Math.min(end, seconds));
    setCurrent(audio.currentTime);
  };

  return (
    <div className="chapter-audio">
      <h3 className="chapter-audio-heading">{heading}</h3>

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(event) => setCurrent(event.target.currentTime)}
        onLoadedMetadata={(event) => setDuration(event.target.duration)}
        onDurationChange={(event) => setDuration(event.target.duration)}
      />

      <div className="chapter-audio-controls">
        <button type="button" className="chapter-audio-skip" onClick={() => seekTo(audioRef.current.currentTime - SKIP_SECONDS)} aria-label={`Back ${SKIP_SECONDS} seconds`}>
          « {SKIP_SECONDS}s
        </button>

        <button type="button" className="chapter-audio-play" onClick={togglePlay} aria-label={playing ? 'Pause' : 'Play'}>
          {playing ? '❚❚' : '▶'}
        </button>

        <button type="button" className="chapter-audio-skip" onClick={() => seekTo(audioRef.current.currentTime + SKIP_SECONDS)} aria-label={`Forward ${SKIP_SECONDS} seconds`}>
          {SKIP_SECONDS}s »
        </button>
      </div>

      <div className="chapter-audio-progress">
        <span className="chapter-audio-time">{formatTime(current)}</span>
        <input
          type="range"
          min="0"
          max={duration || 0}
          step="1"
          value={Math.min(current, duration || 0)}
          onChange={(event) => seekTo(Number(event.target.value))}
          aria-label="Position in the chapter"
        />
        <span className="chapter-audio-time">{formatTime(duration)}</span>
      </div>
    </div>
  );
};

export default ChapterAudioPlayer;
